// The Code workspace's left panel: the project's scripts (filterable, with
// rename/duplicate/run-on-start/delete on each) and, beneath them, the
// active script's outline - exactly Godot's script panel layout (open files
// above, "Outline" of the current file below, one filter for each).
import { useEffect, useMemo, useRef, useState } from 'react';

import { computeHollowScriptOutline, type OutlineItem, type OutlineItemKind } from '../../../engine/scripting/monaco/outline';
import { buildScriptModules, scriptModulePath, uniqueScriptName } from '../../../engine/scripting/scriptModules';
import { useEditorHost } from '../EditorHost';
import { EdConfirmDialog } from '../editor-panels';
import { revealInScript } from '../scripting/editorCommands';
import { countProblems, useScriptProblemsStore } from '../scripting/scriptProblemsStore';
import { isScriptDirty, useScriptStore } from '../../scripting/scriptStore';
import { releaseHollowScriptFileModel } from '../scripting/monaco/HollowScriptFileEditor';
import { hollowScriptAnalysisKey } from '../scripting/monaco/monacoAdapter';
import { useProjectScriptCapabilities } from './useProjectScriptCapabilities';
import type { ScriptDocument } from '../../services/scripts';
import { CodeIcon, type CodeIconKind } from './codeIcons';
import { CwMenu, CwMenuDivider, CwMenuHeading, CwMenuItem, CwSearchInput, CwSectionLabel, CwToolButton, ScriptFileName } from './codeShared';
import { SCRIPT_TEMPLATES, type ScriptTemplate } from './scriptTemplates';

/** Mounts once (the file list is always present in the Code preset) and
 * keeps the roster loaded for whichever project is currently active. */
function useLoadScriptRoster(projectId: string) {
  const loadRoster = useScriptStore((s) => s.loadRoster);
  const rosterLoaded = useScriptStore((s) => s.rosterLoaded);
  const rosterProjectId = useScriptStore((s) => s.rosterProjectId);
  useEffect(() => {
    if (!rosterLoaded || rosterProjectId !== projectId) void loadRoster();
  }, [projectId, rosterLoaded, rosterProjectId, loadRoster]);
}

const OUTLINE_ICON: Record<OutlineItemKind, CodeIconKind> = {
  function: 'function',
  record: 'record',
  enum: 'record',
  constant: 'constant',
  variable: 'variable',
  uses: 'uses',
  import: 'import',
  field: 'variable',
  enumMember: 'constant',
};

const OUTLINE_DEBOUNCE_MS = 200;

/** The active script's outline, from the native analysis handle. The handle
 * is asked for asynchronously (the tooling wasm loads lazily, and a check of
 * a large script should never block a render), so the previous outline stays
 * on screen while a new one is worked out, and a slow answer for text that
 * has since changed is dropped. The handle itself is the editor model's own
 * (`hollowScriptAnalysisKey`), so the panel and the editor share one rather
 * than each holding a live copy of the same checked program. */
function useScriptOutline(active: ScriptDocument | undefined, scripts: readonly ScriptDocument[]): readonly OutlineItem[] {
  const { capabilities } = useProjectScriptCapabilities();
  const [outline, setOutline] = useState<{ readonly scriptId: string | undefined; readonly items: readonly OutlineItem[] }>({ scriptId: undefined, items: [] });
  const activeId = active?.id;
  const name = active?.name;
  const source = active?.source;

  useEffect(() => {
    if (activeId === undefined || name === undefined || source === undefined) {
      setOutline({ scriptId: undefined, items: [] });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      computeHollowScriptOutline({
        source,
        entryPath: scriptModulePath(name),
        grantedCapabilities: capabilities,
        modules: buildScriptModules(scripts, name),
        key: hollowScriptAnalysisKey(activeId),
      }).then(
        (items) => {
          if (!cancelled) setOutline({ scriptId: activeId, items });
        },
        () => undefined,
      );
    }, OUTLINE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeId, name, source, capabilities, scripts]);

  // Another script's outline is never shown for this one while it loads.
  return outline.scriptId === activeId ? outline.items : [];
}

/** New script: picks a template, names the file uniquely, opens it, and
 * drops straight into renaming so the default name is one keystroke from
 * being the creator's own. Shared by the list's "+" menu and the editor's
 * empty state. */
export function createScriptFromTemplate(template: ScriptTemplate): string {
  const store = useScriptStore.getState();
  const name = uniqueScriptName(template.defaultName, new Set(store.scripts.map((s) => s.name)));
  return store.createScript(name, { source: template.source, runOnStart: template.runOnStart });
}

export function NewScriptMenu({ open, onClose, onCreated, align = 'right' }: { open: boolean; onClose: () => void; onCreated?: (id: string) => void; align?: 'left' | 'right' }) {
  return (
    <CwMenu open={open} onClose={onClose} align={align} width={260}>
      <CwMenuHeading>New script</CwMenuHeading>
      {SCRIPT_TEMPLATES.map((template) => (
        <CwMenuItem
          key={template.id}
          icon={template.id === 'blank' ? 'file' : template.runOnStart ? 'bolt' : 'terminal'}
          label={template.label}
          hint={template.description}
          onClick={() => {
            const id = createScriptFromTemplate(template);
            onClose();
            onCreated?.(id);
          }}
        />
      ))}
    </CwMenu>
  );
}

function ScriptRow({
  script,
  active,
  dirty,
  errors,
  warnings,
  renaming,
  onSelect,
  onStartRename,
  onCommitRename,
  onCancelRename,
  onDuplicate,
  onToggleRunOnStart,
  onDelete,
}: {
  script: ScriptDocument;
  active: boolean;
  dirty: boolean;
  errors: number;
  warnings: number;
  renaming: boolean;
  onSelect: () => void;
  onStartRename: () => void;
  onCommitRename: (name: string) => string | null;
  onCancelRename: () => void;
  onDuplicate: () => void;
  onToggleRunOnStart: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [draft, setDraft] = useState(script.name);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renaming) {
      setDraft(script.name);
      setError(null);
      // Select the name so typing replaces it - one keystroke to a real name.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [renaming, script.name]);

  function commit() {
    const failure = onCommitRename(draft.trim());
    if (failure) setError(failure);
  }

  return (
    <>
      <div
        className={'cw-row' + (active ? ' is-active' : '')}
        onClick={renaming ? undefined : onSelect}
        onDoubleClick={renaming ? undefined : onStartRename}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenuOpen(true);
        }}
        role="button"
        tabIndex={renaming ? -1 : 0}
        onKeyDown={(e) => {
          if (renaming) return;
          if (e.key === 'Enter') onSelect();
          if (e.key === 'F2') onStartRename();
          if (e.key === 'Delete' || e.key === 'Backspace') onDelete();
        }}
        aria-current={active ? 'true' : undefined}
        title={renaming ? undefined : `${script.name}.hws${script.runOnStart ? ' · runs on start' : ''}${dirty ? ' · unsaved edits' : ''}`}
      >
        <span className="cw-row-icon">
          <CodeIcon kind={script.runOnStart ? 'bolt' : 'file'} size={14} />
        </span>
        {renaming ? (
          <input
            ref={inputRef}
            className="cw-row-rename"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setError(null);
            }}
            onBlur={commit}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') onCancelRename();
            }}
            aria-label="Script name"
            spellCheck={false}
          />
        ) : (
          <ScriptFileName name={script.name} className="cw-row-name" />
        )}
        {!renaming && (
          <span className="cw-row-badges">
            {errors > 0 && <span className="cw-pill cw-pill--error" title={`${errors} error${errors === 1 ? '' : 's'}`}>{errors}</span>}
            {errors === 0 && warnings > 0 && <span className="cw-pill cw-pill--warning" title={`${warnings} warning${warnings === 1 ? '' : 's'}`}>{warnings}</span>}
            {dirty && <span className="cw-dot" title="Unsaved edits (saves automatically)" />}
          </span>
        )}
        {!renaming && (
          <div style={{ position: 'relative', display: 'flex' }}>
            <button
              type="button"
              className={'cw-row-more' + (menuOpen ? ' is-open' : '')}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              aria-label={`Actions for ${script.name}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <CodeIcon kind="more" size={14} />
            </button>
            <CwMenu open={menuOpen} onClose={() => setMenuOpen(false)} width={230}>
              <CwMenuItem icon="pencil" label="Rename" shortcut="F2" onClick={() => { setMenuOpen(false); onStartRename(); }} />
              <CwMenuItem icon="copy" label="Duplicate" onClick={() => { setMenuOpen(false); onDuplicate(); }} />
              <CwMenuItem
                label="Run on start"
                hint="Runs automatically when Preview or the game starts."
                checked={script.runOnStart}
                onClick={() => { setMenuOpen(false); onToggleRunOnStart(); }}
              />
              <CwMenuDivider />
              <CwMenuItem icon="trash" label="Delete" danger onClick={() => { setMenuOpen(false); onDelete(); }} />
            </CwMenu>
          </div>
        )}
      </div>
      {renaming && error && <div className="cw-row-error">{error}</div>}
    </>
  );
}

/** The right-hand text of an outline row: a function's parameters and result,
 * a constant's or variable's type, an import's names, otherwise the kind. */
function outlineRowDetail(item: OutlineItem): string {
  switch (item.kind) {
    case 'function':
      return item.detail.replace(/^fn/, '');
    case 'constant':
    case 'variable':
    case 'import':
      return item.detail === '' ? item.kind : item.detail;
    default:
      return item.kind;
  }
}

function OutlineRow({ item, onClick }: { item: OutlineItem; onClick: () => void }) {
  return (
    <div
      className="cw-row"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      title={[item.detail, `line ${item.line}`].filter((part) => part !== '').join(' · ')}
    >
      <span className="cw-row-icon">
        <CodeIcon kind={OUTLINE_ICON[item.kind]} size={14} />
      </span>
      <span className="cw-row-name" style={{ fontSize: 11.5 }}>
        {item.name}
        {item.exported && <span className="cw-ext"> · export</span>}
      </span>
      <span className="cw-row-detail" style={{ maxWidth: '45%' }}>
        {outlineRowDetail(item)}
      </span>
    </div>
  );
}

export function CodeFileListPanel() {
  const { projectId } = useEditorHost();
  useLoadScriptRoster(projectId);
  const scripts = useScriptStore((s) => s.scripts);
  const sourceScripts = useScriptStore((s) => s.sourceScripts);
  const activeScriptId = useScriptStore((s) => s.activeScriptId);
  const rosterLoaded = useScriptStore((s) => s.rosterLoaded);
  const saveState = useScriptStore((s) => s.saveState);
  const statusMessage = useScriptStore((s) => s.statusMessage);
  const openScript = useScriptStore((s) => s.openScript);
  const renameScript = useScriptStore((s) => s.renameScript);
  const duplicateScript = useScriptStore((s) => s.duplicateScript);
  const setRunOnStart = useScriptStore((s) => s.setRunOnStart);
  const deleteScript = useScriptStore((s) => s.deleteScript);
  const problemsByScript = useScriptProblemsStore((s) => s.byScript);

  const [filter, setFilter] = useState('');
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ScriptDocument | null>(null);

  const sorted = useMemo(() => [...scripts].sort((a, b) => a.name.localeCompare(b.name)), [scripts]);
  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return needle ? sorted.filter((s) => s.name.toLowerCase().includes(needle)) : sorted;
  }, [sorted, filter]);

  const active = scripts.find((s) => s.id === activeScriptId);
  const outline = useScriptOutline(active, scripts);

  /** Only disposes the Monaco model (and its undo history) once the delete
   * has actually gone through - releasing it eagerly, before knowing whether
   * the server call succeeded, meant a failed/offline delete restored the
   * script to the roster with its undo history already gone. */
  function removeScript(script: ScriptDocument) {
    void deleteScript(script.id).then(() => {
      const stillPresent = useScriptStore.getState().scripts.some((s) => s.id === script.id);
      if (!stillPresent) releaseHollowScriptFileModel(script.id);
    });
  }

  const loading = !rosterLoaded && saveState === 'saving';
  const loadFailed = !rosterLoaded && saveState === 'error';

  return (
    <div className="cw-fill cw-fill--side">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 10px 0' }}>
        <CwSearchInput value={filter} onChange={setFilter} placeholder="Filter scripts" ariaLabel="Filter scripts" />
        <div style={{ position: 'relative' }}>
          <CwToolButton icon="plus" label="New script" showLabel={false} onClick={() => setNewMenuOpen((o) => !o)} active={newMenuOpen} />
          <NewScriptMenu open={newMenuOpen} onClose={() => setNewMenuOpen(false)} onCreated={(id) => setRenamingId(id)} />
        </div>
      </div>

      <CwSectionLabel count={scripts.length || undefined}>Scripts</CwSectionLabel>
      <div className="cw-list cw-list--scroll" style={{ flex: '1 1 0' }} role="list">
        {loading && <div className="cw-hint">Loading your scripts...</div>}
        {loadFailed && <div className="cw-hint" style={{ color: 'var(--ss-danger)' }}>{statusMessage ?? "Couldn't load your scripts."}</div>}
        {rosterLoaded && scripts.length === 0 && (
          <div className="cw-hint">No scripts yet. Press + to create one from a template, or start from a blank file.</div>
        )}
        {rosterLoaded && scripts.length > 0 && visible.length === 0 && <div className="cw-hint">No script matches "{filter}".</div>}
        {visible.map((script) => {
          const counts = countProblems(problemsByScript[script.id]);
          return (
            <ScriptRow
              key={script.id}
              script={script}
              active={script.id === activeScriptId}
              dirty={isScriptDirty({ scripts, sourceScripts }, script.id)}
              errors={counts.errors}
              warnings={counts.warnings}
              renaming={renamingId === script.id}
              onSelect={() => openScript(script.id)}
              onStartRename={() => setRenamingId(script.id)}
              onCommitRename={(name) => {
                const result = renameScript(script.id, name);
                if (result.ok) {
                  setRenamingId(null);
                  return null;
                }
                return result.error;
              }}
              onCancelRename={() => setRenamingId(null)}
              onDuplicate={() => {
                const id = duplicateScript(script.id);
                if (id) setRenamingId(id);
              }}
              onToggleRunOnStart={() => setRunOnStart(script.id, !script.runOnStart)}
              onDelete={() => setPendingDelete(script)}
            />
          );
        })}
      </div>

      <CwSectionLabel count={outline.length || undefined}>Outline</CwSectionLabel>
      <div className="cw-list cw-list--scroll" style={{ flex: '0 1 auto', maxHeight: '40%', paddingBottom: 12 }} role="list">
        {!active && <div className="cw-hint">Open a script to see its functions and declarations here.</div>}
        {active && outline.length === 0 && <div className="cw-hint">No declarations yet. Functions, records, constants and variables you declare at the top level appear here.</div>}
        {active && outline.map((item, i) => <OutlineRow key={`${item.kind}:${item.name}:${i}`} item={item} onClick={() => revealInScript(active.id, item.selLine, item.selColumn)} />)}
      </div>

      {pendingDelete && (
        <EdConfirmDialog
          title={`Delete ${pendingDelete.name}.hws?`}
          body={
            pendingDelete.runOnStart
              ? 'This script runs when the game starts. Deleting it removes it from the project for good; other scripts that import it will stop checking.'
              : 'This removes the script from the project for good. Other scripts that import it will stop checking.'
          }
          confirmLabel="Delete script"
          onConfirm={() => {
            removeScript(pendingDelete);
            setPendingDelete(null);
          }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
